<template>
  <div
    class="fm-root"
    :class="{ 'fm-drag-over': isDraggingOver }"
    @dragover.prevent="checkExternalDrag"
    @dragleave="isDraggingOver = false"
    @drop.prevent="onDropRoot"
  >
    <FMToolbar
      :current-path="currentPath"
      :view-mode="viewMode"
      :sort-by="sortBy"
      :sort-dir="sortDir"
			:show-deleted="showDeleted"
      @go-back="goBack"
      @go-home="goHome"
      @navigate="navigateTo"
      @create-folder="showCreateFolder = true"
      @upload="triggerUpload"
      @set-view="viewMode = $event"
      @set-sort="setSort"
      @search="searchQuery = $event"
			@set-show-deleted="setShowDeleted"
			@reload="fetchFiles"
			@toggle-sidebar="showSidebar = !showSidebar"
    />

    <div class="fm-body">
      <FMSidebar
				v-if="showSidebar"
        :folders="sidebarFolders"
        :current-folder-name="sidebarFolderName"
        :current-path="sidebarCurrentPath"
				:storage-used="storageUsed"
				:storage-total="storageTotal"
        @navigate="onSidebarNavigate"
        @go-back="sidebarGoBack"
      />

      <div class="fm-main">
        <FMBreadcrumb
          :path="currentPath"
          @navigate="navigateTo"
        />

        <FMDropZone
          :active="isDraggingOver"
          @drop="onDropRoot"
        />


        <div v-if="folderLoading" class="fm-folder-loading">
          <v-progress-circular
            indeterminate
            color="primary"
            :size="40"
          />
        </div>
        <FMEmpty
          v-else-if="filteredItems.length === 0"
          :search="searchQuery"
        />
        <FMGrid
          v-else-if="viewMode === 'grid'"
          :items="filteredItems"
          :selected="selected"
          :drag-target="dragTarget"
					:show-deleted="showDeleted"
          @open="openItem"
          @select="toggleSelect"
          @select-only="selectOnly"
          @context="showContext"
          @drag-start="onDragStart"
          @drag-over-item="dragTarget = $event"
          @drop-on-item="moveInto"
        />
        <FMList
          v-else
          :items="filteredItems"
          :selected="selected"
          :sort-by="sortBy"
          :sort-dir="sortDir"
          :drag-target="dragTarget"
					:show-deleted="showDeleted"
          @open="openItem"
          @select="toggleSelect"
          @select-only="selectOnly"
          @context="showContext"
          @drag-start="onDragStart"
          @drag-over-item="dragTarget = $event"
          @drop-on-item="moveInto"
          @set-sort="setSort"
        />
      </div>
    </div>

    <FMStatusBar
      :total="currentItems.length"
      :selected-count="selected.length"
      :selected-items="selectedItems"
      @download="downloadSelected"
    />

    <FMCreateFolder
      v-if="showCreateFolder"
      @confirm="createFolder"
      @cancel="showCreateFolder = false"
    />

    <FMRename
      v-if="showRenameModal"
      :item="itemToRename"
      @confirm="confirmRename"
      @cancel="showRenameModal = false"
    />

    <FMContextMenu
      v-if="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :item="contextMenu.item"
      :selected-count="selected.length"
			:forge-supported-extensions="forgeSupportedExtensions"
			@open-morphable="openItemMorphable"
      @rename="startRename"
      @delete="deleteSelected"
      @restore="restoreSelected"
      @move="startMove"
      @download="downloadItem"
      @open="openItemInNewWindow"
			@to-forge="addItemToForge"
			@view-in-forge="openItem"
			@add-revision="handleAddRevision"
      @close="contextMenu.visible = false"
    />

    <FMMoveDialog
      v-if="showMoveDialog"
      :tree="folderTree"
      :current-path="currentPath"
      @confirm="moveSelected"
      @cancel="showMoveDialog = false"
    />

    <FMAddRevision
      v-if="showAddRevisionDialog"
      :item="revisionFileItem"
      @confirm="uploadRevision"
      @cancel="showAddRevisionDialog = false"
    />

    <input
      ref="fileInput"
      type="file"
      multiple
      style="display:none"
      @change="onFileInputChange"
    />

    <GlobalFileViewer
      :media-items="mediaItems"
      :media-index="selectedImageIndex"
      :document-item="selectedDocumentItem"
      :forge-item="selectedForgeItem"
      :document-revision-context="selectedDocumentRevisionContext"
      :forge-revision-context="selectedForgeRevisionContext"
      :revision-loading="revisionContextLoading"
			@open-morphable="openItemMorphable"
			@minimize-file="minimizeItem"
      @close="closeViewers"
			@onChange="onChangeImageIndex"
      @select-revision="selectViewerRevision"
      @error="onDocumentLoadError"
      @load="onDocumentLoadSuccess"
    />

    <AcceptCancelModal
      :acceptRejectModal="deleteConfirmModal"
      :acceptRejectModalData="deleteConfirmData"
      :closeModal="handleDeleteConfirmation"
    />

    <AcceptCancelModal
      :acceptRejectModal="forgeConfirmModal"
      :acceptRejectModalData="forgeConfirmData"
      :closeModal="handleForgeConfirmation"
    />

    <div v-if="loading" class="fm-loader">
			<v-progress-circular
				indeterminate
				color="white"
				:size="50"
			></v-progress-circular>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import EventBus from '@/helpers/event_bus'
import {getProjectRequest, isCurrentProjectRequest} from '@/helpers/projectContext'
import { checkPermissions } from '@/helpers/permissions'
import {getTreePath, getFile, createFolder, updateNode, changeNodesParent, deleteNodes, restoreNodes} from '@/helpers/api/files'
import { getZippedFiles } from '@/helpers/api/fileUploads'
import { getPublicUrl } from '@/helpers/api/tasks'
import { getSupportedExtensions, addFileToForge } from '@/helpers/api/forge'
import { getParentElement } from '@/helpers/api/general'
import { isForgeItem as isForgeCompatible } from '@/helpers/forge'

// import { dummyData } from './dummyData'
import FMToolbar from './FMToolbar.vue'
import FMSidebar from './FMSidebar.vue'
import FMBreadcrumb from './FMBreadcrumb.vue'
import FMGrid from './FMGrid.vue'
import FMList from './FMList.vue'
import FMEmpty from './FMEmpty.vue'
import FMDropZone from './FMDropZone.vue'
import FMStatusBar from './FMStatusBar.vue'
import FMCreateFolder from './FMCreateFolder.vue'
import FMRename from './FMRename.vue'
import FMContextMenu from './FMContextMenu.vue'
import FMMoveDialog from './FMMoveDialog.vue'
import FMAddRevision from './FMAddRevision.vue'

import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal'

export default {
  name: 'FileManager',

  components: {
    FMToolbar,
    FMSidebar,
    FMBreadcrumb,
    FMGrid,
    FMList,
    FMEmpty,
    FMDropZone,
    FMStatusBar,
    FMCreateFolder,
    FMRename,
    FMContextMenu,
    FMMoveDialog,
    FMAddRevision,
		GlobalFileViewer,
    AcceptCancelModal
  },

  provide() {
    return {
      getFileById: (id) => this.files.find(f => f.uid === id) || this.folderStack.find(f => f.uid === id)
    }
  },

  data() {
    return {
      files: [],
			storageUsed: 0,
			storageTotal: 0,
			selectedImageIndex: null,
      currentPath: [null],
      viewMode: 'list',
      sortBy: 'created_at',
      sortDir: 'desc',
      selected: [],
      searchQuery: '',
      isDraggingOver: false,
      draggedIds: [],
      dragTarget: null,
      showCreateFolder: false,
      showMoveDialog: false,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        item: null
      },
      renamingId: null,
      loading: true,
      showRenameModal: false,
      itemToRename: null,
      selectedDocumentItem: null,
      selectedDocumentRevisionContext: null,
			selectedForgeItem: null,
      selectedForgeRevisionContext: null,
      revisionContextLoading: false,
      revisionRequestId: 0,
			forgeSupportedExtensions: [],
			deleteConfirmModal: false,
			deleteConfirmData: null,
			forgeConfirmModal: false,
			forgeConfirmData: null,
			forgeItemToSend: null,
			showSidebar: true,
			revisionFileItem: null,
			showAddRevisionDialog: false,
			folderLoading: false,
			folderStack: [],
			sidebarStack: [],
			sidebarFolders: [],
			showDeleted: false
    }
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    currentFolderUid() {
      return this.currentPath[this.currentPath.length - 1]
    },
		currentFolderId(){
			return this.folderStack.length > 0 ? this.folderStack[this.folderStack.length - 1].id : null
		},
		currentFolderName(){
			return this.folderStack.length > 0 ? this.folderStack[this.folderStack.length - 1].name : null
		},
		sidebarFolderName(){
			return this.sidebarStack.length > 0 ? this.sidebarStack[this.sidebarStack.length - 1].name : null
		},
		sidebarCurrentPath(){
			return [null, ...this.sidebarStack.map(f => f.uid)]
		},
    currentItems() {
      return this.files.filter(f => f.parent_uid === this.currentFolderUid)
    },
		mediaItems() {
			const mediaItems = this.currentItems.filter(item => item.pathmedia).map((item) => {
				return {
					src: item.pathmedia.path,
					downloadable_url: item.downloadable_url,
					mime_type: item.mime_type,
					ext: item.ext,
					size: item.size,
					name: item.name,
					uid: item.uid,
					autoplay: true,
					item
				}
			})
			return mediaItems
		},
    filteredItems() {
      let items = this.currentItems

			if(!this.showDeleted){
				items = items.filter(f => !f.deleted_at)
			}

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        items = items.filter(f => f.name.toLowerCase().includes(q))
      }

      return [...items].sort((a, b) => {
        if (a.type === 'folder' && b.type !== 'folder') {
          return -1
        }
        if (a.type !== 'folder' && b.type === 'folder') {
          return 1
        }

        let va = a[this.sortBy],
            vb = b[this.sortBy]
				if(this.sotrBy === 'createdBy'){
					va = a.user?.name || ''
					vb = b.user?.name || ''
				}
				if(this.sotrBy === 'deletedBy'){
					va = a.deleted_by?.name || ''
					vb = b.deleted_by?.name || ''
				}

        if (typeof va === 'string' && typeof vb === 'string') {
          va = va.toLowerCase()
          vb = vb.toLowerCase()
        }

        if (va < vb) {
          return this.sortDir === 'asc' ? -1 : 1
        }
        if (va > vb) {
          return this.sortDir === 'asc' ? 1 : -1
        }

        return 0
      })
    },

    selectedItems() {
      return this.files.filter(f => this.selected.includes(f.uid))
    }
  },
	watch: {
    selectedProject(newVal, oldVal){
      if(newVal?.id !== oldVal?.id){
        this.resetState()
      }
      if(newVal?.id){
				this.fetchFiles()
			}
		}
	},
	mounted(){
		if(this.selectedProject?.id){
			this.fetchFiles()
		}
		this.fileListeners()
		this.getForgeSupportedExtensions()
		this.$nextTick(() => {
			setTimeout(() => {
				this.showSidebar = !this.isMobile
			}, 500)
		})
	},
	beforeUnmount() {
		EventBus.$off('completedFileUpload')
	},
  methods: {
    checkPermission(permissionsToCheck, actionName) {
      // Usa la función checkPermissions que existe en permissions.js
      if (!checkPermissions(Array.isArray(permissionsToCheck) ? permissionsToCheck : [permissionsToCheck])) {
        this.$store.globalStore.dispatch('addMessage', {
          message: `No tienes permisos para ${actionName || 'realizar esta acción'}.`,
          title: 'Acceso denegado',
          created: new Date(),
          type: 'warning',
          icon: 'mdi-lock-outline',
          color: '#FB8500',
        })
        return false
      }
      return true
    },
		resetState(){
			this.files = []
			this.storageUsed = 0
			this.storageTotal = 0
			this.currentPath = [null]
			this.viewMode = 'list'
			this.sortBy = 'created_at'
			this.sortDir = 'desc'
			this.selected = []
			this.searchQuery = ''
			this.isDraggingOver = false
			this.draggedIds = []
			this.dragTarget = null
			this.showCreateFolder = false
			this.showMoveDialog = false
			this.contextMenu = {
				visible: false,
				x: 0,
				y: 0,
				item: null
			}
			this.renamingId = null
			this.loading = true
			this.showRenameModal = false
			this.itemToRename = null
			this.selectedDocumentItem = null
      this.selectedDocumentRevisionContext = null
			this.selectedForgeItem = null
      this.selectedForgeRevisionContext = null
      this.revisionContextLoading = false
      this.revisionRequestId += 1
			this.deleteConfirmModal = false
			this.deleteConfirmData = null
			this.forgeConfirmModal = false
			this.forgeConfirmData = null
			this.forgeItemToSend = null
			this.folderLoading = false
			this.folderStack = []
			this.sidebarStack = []
			this.sidebarFolders = []
		},
    fileListeners(){
      EventBus.$on('completedFileUpload', async () => {
				this.fetchFiles(false)
      })
    },
		async fetchFiles(showLoader = true, updateSidebar = true) {
      const request = getProjectRequest(this.selectedProject?.id)
			if (showLoader) {
				this.loading = true
			} else {
				this.folderLoading = true
			}
			const data = {
				project_id: this.selectedProject.id,
				parent_id: this.currentFolderId
			}
			const res = await getTreePath(data)
      if(isCurrentProjectRequest(request.projectId, request.generation) && res.code == 200){
				this.files = res.data
				if (updateSidebar) {
					this.sidebarFolders = res.data.filter(f => f.type === 'folder')
				}
				this.storageUsed = res.total_files_size
				this.storageTotal = res.project_storage_limit
			}
			else{
				console.log('error')
			}
      if(isCurrentProjectRequest(request.projectId, request.generation)){
        this.loading = false
        this.folderLoading = false
      }
		},
		getNodeId(uid){
			const f = this.files.find(x => x.uid === uid)
			return f ? f.id : null
		},

		onSidebarNavigate(uid) {
			const folder = this.sidebarFolders.find(f => f.uid === uid) || this.files.find(f => f.uid === uid && f.type === 'folder')
			if (!folder) return
			const hasChildren = (folder.childs_count || 0) > 0
			this.folderStack = [...this.sidebarStack, { uid: folder.uid, id: folder.id, name: folder.name }]
			this.currentPath = [null, ...this.folderStack.map(f => f.uid)]
			if (hasChildren) {
				this.sidebarStack = [...this.sidebarStack, { uid: folder.uid, id: folder.id, name: folder.name }]
			}
			this.selected = []
			this.searchQuery = ''
			this.fetchFiles(false, hasChildren)
		},

		sidebarGoBack() {
			if (this.sidebarStack.length === 0) return
			this.sidebarStack.pop()
			this.folderStack = [...this.sidebarStack]
			this.currentPath = [null, ...this.sidebarStack.map(f => f.uid)]
			this.selected = []
			this.fetchFiles(false, true)
		},

		getForgeSupportedExtensions(){
			getSupportedExtensions().then(response => {
				this.forgeSupportedExtensions = response.supported_extensions.map(ext => `${ext}`)
			})
		},

		onChangeImageIndex(newIndex){
			console.log('new index', newIndex)
		},

    isDocumentType(ext) {
      if (!ext) return false
      const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
      return documentTypes.includes(ext.toLowerCase())
    },

    openItem(item) {
      if (item.type === 'folder') {
        const hasChildren = (item.childs_count || 0) > 0
        this.folderStack.push({ uid: item.uid, id: item.id, name: item.name })
        this.currentPath.push(item.uid)
        if (hasChildren) {
          this.sidebarStack.push({ uid: item.uid, id: item.id, name: item.name })
        }
        this.selected = []
        this.searchQuery = ''
        this.fetchFiles(false, hasChildren)
        return
      }
			else if (item.pathmedia){
				const imageIndex = this.mediaItems.findIndex(i => i.uid === item.uid)
				if(imageIndex !== -1){
					this.selectedImageIndex = imageIndex
				}
			}
      else if (item.pathdoc) {
        this.selectedDocumentItem = item
        this.selectedDocumentRevisionContext = null
        this.loadRevisionContext(item, 'document')
      }
			else if(item.forge_file){
				if(item.forge_file.translated){
					this.selectedForgeItem = item
          this.selectedForgeRevisionContext = null
          this.loadRevisionContext(item, 'forge')
				}
				else{
					this.forgeItemToSend = item
					this.forgeConfirmData = {
						title: 'Enviar a visor Autodesk',
						content_a: `¿Deseas enviar "${item.name}" al visor Autodesk?`,
						content_b: 'El archivo será procesado y podrá visualizarse después.'
					}
					this.forgeConfirmModal = true
				}
			}
			else if(this.isForgeItem(item)){
				this.forgeItemToSend = item
				this.forgeConfirmData = {
					title: 'Enviar a visor Autodesk',
					content_a: `¿Deseas enviar "${item.name}" al visor Autodesk?`,
					content_b: 'El archivo será procesado y podrá visualizarse después.'
				}
				this.forgeConfirmModal = true
			}
			this.contextMenu.visible = false
    },

    async loadRevisionContext(item, viewerType) {
      const requestId = ++this.revisionRequestId
      this.revisionContextLoading = true
      try {
        const response = await getFile({ file_id: item.id })
        if (requestId !== this.revisionRequestId) return
        const context = response.code === 200 ? response.revision_context : null
        if (viewerType === 'document' && this.selectedDocumentItem?.id === item.id) {
          this.selectedDocumentRevisionContext = context
        }
        if (viewerType === 'forge' && this.selectedForgeItem?.id === item.id) {
          this.selectedForgeRevisionContext = context
        }
      } catch (error) {
        if (requestId === this.revisionRequestId) {
          if (viewerType === 'document') this.selectedDocumentRevisionContext = null
          if (viewerType === 'forge') this.selectedForgeRevisionContext = null
        }
      } finally {
        if (requestId === this.revisionRequestId) {
          this.revisionContextLoading = false
        }
      }
    },

    selectDocumentRevision(item) {
      if (item?.id) this.selectedDocumentItem = item
    },

    selectViewerRevision(item) {
      if (this.selectedForgeItem) {
        this.selectForgeRevision(item)
        return
      }
      this.selectDocumentRevision(item)
    },

    selectForgeRevision(item) {
      if (!item?.id) return
      if (item.forge_file?.translated) {
        this.selectedForgeItem = item
        this.$store.forgeStore.commit('setForgeSelectedFile', item)
        return
      }
      this.forgeItemToSend = item
      this.forgeConfirmData = {
        title: 'Enviar a visor Autodesk',
        content_a: `¿Deseas enviar "${item.name}" al visor Autodesk?`,
        content_b: 'El archivo será procesado y podrá visualizarse después.'
      }
      this.forgeConfirmModal = true
    },

    closeViewers() {
      this.selectedImageIndex = null
      this.selectedDocumentItem = null
      this.selectedForgeItem = null
    },

		async openItemMorphable(item){
			const typeMap = {
				'App\\Models\\TasksRestrictions': 'task',
				'App\\Models\\TasksRestrictionsInteractions': 'task_interaction',
				'App\\Models\\Rdi': 'rdi',
				'App\\Models\\RdiInteractions': 'rdi_interaction',
				'App\\Models\\Additional': 'additional',
				'App\\Models\\AdditionalRevision': 'additional_revision',
				'App\\Models\\AdditionalObservation': 'additional_observation'
			}
			const parentTypesMap = {
				'App\\Models\\TasksRestrictions': 'task',
				'App\\Models\\TasksRestrictionsInteractions': 'task',
				'App\\Models\\Rdi': 'rdi',
				'App\\Models\\RdiInteractions': 'rdi',
				'App\\Models\\Additional': 'additional',
				'App\\Models\\AdditionalRevision': 'additional',
				'App\\Models\\AdditionalObservation': 'additional'
			}
			if(!typeMap[item.morphable_type]){
				return
			}
			const data = {
				item_id: item.morphable_id,
				item_type: typeMap[item.morphable_type]
			}
			if(parentTypesMap[item.morphable_type] == 'task'){
				this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
			}
			else if(parentTypesMap[item.morphable_type] == 'rdi'){
				this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
			}
			else if(parentTypesMap[item.morphable_type] == 'additional'){
				this.$store.adicionalesStore.commit('setCurrentAdicional', { loading: true })
			}
			this.contextMenu.visible = false

			const element = await getParentElement(data)
			if(element.code == 200){
				if(parentTypesMap[item.morphable_type] == 'task'){
					this.$store.consultasStore.commit('setCurrentTarea', element.task)
				}
				else if(parentTypesMap[item.morphable_type] == 'rdi'){
					this.$store.rdiStore.commit('setCurrentRDI', element.rdi)
				}
				else if(parentTypesMap[item.morphable_type] == 'additional'){
					this.$store.adicionalesStore.commit('setCurrentAdicional', element.additional)
				}
			}
		},

		openItemInNewWindow(){
			const item = this.contextMenu.item
			const width = window.outerWidth * 0.9;
			const height = window.outerHeight * 0.9;
			const left = window.screenX;
			const top = window.screenY;
			if(item.pathdoc){
				const url = 'document-viewer?item_id=' + item.id;
				window.open(
					url,
					'_blank',
					`width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
				);
			}
			else if(item.forge_file){
				if(item.forge_file.translated){
					const url = 'forge-viewer?item_id=' + item.id;
					window.open(
						url,
						'_blank',
						`width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
					);
				}
				else{
					this.forgeItemToSend = item
					this.forgeConfirmData = {
						title: 'Enviar a visor Autodesk',
						content_a: `¿Deseas enviar "${item.name}" al visor Autodesk?`,
						content_b: 'El archivo será procesado y podrá visualizarse después.'
					}
					this.forgeConfirmModal = true
				}
			}
			else if(this.isForgeItem(item)){
				this.forgeItemToSend = item
				this.forgeConfirmData = {
					title: 'Enviar a visor Autodesk',
					content_a: `¿Deseas enviar "${item.name}" al visor Autodesk?`,
					content_b: 'El archivo será procesado y podrá visualizarse después.'
				}
				this.forgeConfirmModal = true
			}
		},

		minimizeItem(item){
			this.$store.globalStore.commit('addFileToFileManagerHandler', item)
			this.selectedImageIndex = null
			this.selectedDocumentItem = null
			this.selectedForgeItem = null
		},

		isForgeItem(item){
      return isForgeCompatible(item, this.forgeSupportedExtensions)
		},

		addItemToForge(item){
			if (!this.checkPermission('send_to_autodesk', 'enviar a visor Autodesk')) {
				this.contextMenu.visible = false
				return
			}
			this.contextMenu.visible = false
			addFileToForge({file_id: item.id}).then(response => {
				if(response.code === 200){
          const fileName = item.name || item.title || item.filename || `Archivo ${item.id}`
          this.$store.globalStore.commit('setForgeProcessingFile', {
            file_id: item.id,
            file_name: fileName,
            title: fileName
          })
					this.fetchFiles()
				}
			})
		},

    navigateTo(p) {
      if (Array.isArray(p)) {
        this.currentPath = p
        this.folderStack = this.folderStack.slice(0, p.length - 1)
        this.sidebarStack = this.sidebarStack.slice(0, p.length - 1)
      } else {
        const i = this.currentPath.indexOf(p)
        this.currentPath = i !== -1 ? this.currentPath.slice(0, i + 1) : [null, p]
        this.folderStack = this.folderStack.slice(0, this.currentPath.length - 1)
        this.sidebarStack = this.sidebarStack.slice(0, this.currentPath.length - 1)
      }
      this.selected = []
      this.searchQuery = ''
      this.fetchFiles(false, true)
    },

    goBack() {
      if (this.currentPath.length > 1) {
        this.folderStack.pop()
        this.currentPath.pop()
        if (this.sidebarStack.length > this.folderStack.length) {
          this.sidebarStack.pop()
        }
        this.fetchFiles(false, true)
      }
    },

    goHome() {
      this.currentPath = [null]
      this.folderStack = []
      this.sidebarStack = []
      this.sidebarFolders = []
      this.selected = []
      this.fetchFiles(true, true)
    },

    toggleSelect(id) {
      const i = this.selected.indexOf(id)
      if (i === -1) {
        this.selected.push(id)
      } else {
        this.selected.splice(i, 1)
      }
    },

    selectOnly(id) {
      this.selected = [id]
    },

		setShowDeleted(value){
			this.showDeleted = value
			this.viewMode = 'list'
		},

    setSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDir = 'asc'
      }
    },

    async createFolder(name) {
      if (!this.checkPermission(['create_folders'], 'crear carpetas')) {
        this.showCreateFolder = false
        return
      }
			const foder = await createFolder({
				project_id: this.selectedProject.id,
				parent_id: this.currentFolderId,
				name
			})
			if(foder.code == 200){
				this.files.push(foder.data)
			}
			else{
				console.log('error')
			}
      this.showCreateFolder = false
    },

    deleteSelected() {
      if (!this.checkPermission(['delete_files', 'delete_folders'], 'eliminar archivos')) {
        this.contextMenu.visible = false
        return
      }
      const del = new Set(this.selected)
      
      const collectChildren = id => {
        this.files
          .filter(f => f.parent_uid === id)
          .forEach(f => {
            del.add(f.uid)
            if (f.type === 'folder') {
              collectChildren(f.uid)
            }
          })
      }

			let data = []
      this.selected.forEach(id => {
        const f = this.files.find(x => x.uid === id)
				if (f) {
					data.push({id: f.id, type: f.type})
				}
        if (f?.type === 'folder') {
          collectChildren(id)
        }
      })

			this.deleteConfirmData = {
				title: 'Confirmar eliminación',
				content_a: `¿Estás seguro de que deseas eliminar ${this.selected.length} elemento(s)?`,
				content_b: 'Esta acción sólo la pueden revertir los usuarios con permiso para restaurar archivos.'
			}
			this.deleteConfirmModal = true
      this.contextMenu.visible = false
    },


    async restoreSelected() {
			this.contextMenu.visible = false
      const del = new Set(this.selected)
      
      const collectChildren = id => {
        this.files
          .filter(f => f.parent_uid === id)
          .forEach(f => {
            del.add(f.uid)
            if (f.type === 'folder') {
              collectChildren(f.uid)
            }
          })
      }

			let data = []
      this.selected.forEach(id => {
        const f = this.files.find(x => x.uid === id)
				if (f) {
					data.push({id: f.id, type: f.type})
				}
        if (f?.type === 'folder') {
          collectChildren(id)
        }
      })

			const response = await restoreNodes({nodes: data})
			if(response.code === 200){
				this.selected = []
				this.fetchFiles()
			}
    },

    async handleDeleteConfirmation(confirmed) {
      if (confirmed && this.deleteConfirmData) {
        const del = new Set(this.selected)
        
        const collectChildren = id => {
          this.files
            .filter(f => f.parent_uid === id)
            .forEach(f => {
              del.add(f.uid)
              if (f.type === 'folder') {
                collectChildren(f.uid)
              }
            })
        }

        let data = []
        this.selected.forEach(id => {
          const f = this.files.find(x => x.uid === id)
          if (f) {
            data.push({id: f.id, type: f.type})
          }
          if (f?.type === 'folder') {
            collectChildren(id)
          }
        })

        if (data.length > 0) {
          const response = await deleteNodes({nodes: data})
					if(response.code === 200){
						this.selected = []
						this.fetchFiles()
					}
        }

        this.files = this.files.filter(f => !del.has(f.uid))
        this.selected = []
      }
      
      this.deleteConfirmModal = false
      this.deleteConfirmData = null
    },

    handleForgeConfirmation(confirmed) {
      if (confirmed && this.forgeItemToSend) {
        this.addItemToForge(this.forgeItemToSend)
      }
      
      this.forgeConfirmModal = false
      this.forgeConfirmData = null
      this.forgeItemToSend = null
    },

    showContext({ event, item }) {
      if (!this.selected.includes(item.uid)) {
        this.selected = [item.uid]
      }
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        item
      }
    },

    onDragStart(ids) {
      this.draggedIds = ids
    },

    checkExternalDrag(e) {
      const hasFiles = e.dataTransfer.types && e.dataTransfer.types.includes('Files')
      
      if (hasFiles) {
        this.isDraggingOver = true
      } else {
        this.isDraggingOver = false
      }
    },

    onDropRoot(event) {
      this.isDraggingOver = false
      
      // Capturar archivos externos del sistema operativo
      const droppedFiles = event.dataTransfer.files
      if (droppedFiles.length > 0) {
        Array.from(droppedFiles).forEach(file => {
          const newFile = {
            type: 14,
            payload: {
              file_manager: true,
              project_id: this.selectedProject.id,
              folder_id: this.currentFolderId
            },
            total: file.size,
            uuid: uuidv4(),
            file
          }
          this.$store.globalStore.commit('setFilesToUploadCenter', newFile)
        })
      }
      
      // Procesar movimiento de archivos internos
      if (this.draggedIds.length) {
        let nodesToMove = []
        this.draggedIds.forEach(id => {
          const f = this.files.find(x => x.uid === id)
          if (f) {
            nodesToMove.push({id: f.id, type: f.type})
            f.parent_uid = this.currentFolderUid
          }
        })

        this.draggedIds = []
        if(nodesToMove.length){
          const data = {
            nodes: nodesToMove,
            parent_id: this.currentFolderId
          }
          changeNodesParent(data)
        }
      }
    },

    moveInto(targetId) {
      const t = this.files.find(f => f.uid === targetId)
      if (!t || t.type !== 'folder') {
        return
      }

			let nodesToMove = []
      this.draggedIds.forEach(id => {
        if (id !== targetId) {
          const f = this.files.find(x => x.uid === id)
          if (f) {
						nodesToMove.push({id: f.id, type: f.type})
            f.parent_uid = targetId
          }
        }
      })

			const data = {
				nodes: nodesToMove,
				parent_id: this.getNodeId(targetId)
			}
			changeNodesParent(data)
      
      this.draggedIds = []
      this.dragTarget = null
    },

    moveSelected(targetId) {
			let nodesToMove = []
      this.selected.forEach(id => {
        if (id !== targetId) {
          const f = this.files.find(x => x.uid === id)
          if (f) {
						nodesToMove.push({id: f.id, type: f.type})
            f.parent_uid = targetId
          }
        }
      })

			const data = {
				nodes: nodesToMove,
				parent_id: this.getNodeId(targetId)
			}
			changeNodesParent(data)
      this.selected = []
      this.showMoveDialog = false
    },

    triggerUpload() {
      if (!this.checkPermission(['create_files'], 'subir archivos')) {
        return
      }
      this.$refs.fileInput.click()
    },

    onDocumentLoadError() {
      console.error('Document failed to load')
    },

    onDocumentLoadSuccess() {
      console.log('Document loaded successfully')
    },

    onForgeLoadError() {
      console.error('Forge file failed to load')
    },

    onForgeLoadSuccess() {
      console.log('Forge file loaded successfully')
    },

    onFileInputChange(e) {
      if (!this.checkPermission(['create_files'], 'subir archivos')) {
        e.target.value = ''
        return
      }
      Array.from(e.target.files).forEach(file => {
				const newFile = {
					type: 14,
					payload: {
						file_manager: true,
						project_id: this.selectedProject.id,
						folder_id: this.currentFolderId
					},
					total: file.size,
					uuid: uuidv4(),
					file
				}
				this.$store.globalStore.commit('setFilesToUploadCenter', newFile)
      })
      e.target.value = ''
    },

		startMove() {
			if (!this.checkPermission(['move_files'], 'mover archivos')) {
				this.contextMenu.visible = false
				return
			}
			this.showMoveDialog = true
			this.contextMenu.visible = false
		},

    downloadItem(item) {
      if (item.type === 'folder') {
        this.downloadSelected([item])
        this.contextMenu.visible = false
        return
      }

			if(item.pathmedia){
				window.open(item.pathmedia?.path, '_blank')
			}
			else{
				this.getFilesUrl(item, true, true)
			}
      this.contextMenu.visible = false
    },

    async getFilesUrl(attachments, singleFile = false, download = false){
      if(singleFile){ attachments = [attachments] }
      this.loadingFilePath = true
      const files = await getPublicUrl({attachments, force_download: download})
      if(files.code === 200){
        files.files.forEach((file) => {
          window.open(file.url);
        })
      }
      this.loadingFilePath = false
    },

    async downloadSelected(items = this.selectedItems) {
      const selectedItems = items
      const fileIds = selectedItems
        .filter(item => item.type !== 'folder' && item.id)
        .map(item => item.id)
      const folderIds = selectedItems
        .filter(item => item.type === 'folder' && item.id)
        .map(item => item.id)

      if (!fileIds.length && !folderIds.length) {
        return
      }

      this.loading = true
      try {
        const response = await getZippedFiles({
          file_ids: fileIds,
          folder_ids: folderIds
        })

        if (response.code === 200 && response.fileurl) {
          window.open(response.fileurl, '_blank')
        } else if (response.code === 202) {
          this.setMessage({
            message: 'La descarga se está preparando y será enviada por correo.',
            title: 'Descarga en proceso',
            type: 'info',
            icon: 'mdi-download',
            color: '#2D4B95'
          })
        }
      } catch (error) {
        this.setMessage({
          message: 'No fue posible preparar la descarga.',
          title: 'Error de descarga',
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#B03A2E'
        })
      } finally {
        this.loading = false
      }
    },

    startRename(item) {
      if (!this.checkPermission(['rename_files'], 'renombrar archivos')) {
        this.contextMenu.visible = false
        return
      }
      this.itemToRename = item
      this.showRenameModal = true
      this.contextMenu.visible = false
    },

    async confirmRename(newName) {
      if (this.itemToRename) {
        updateNode({id: this.itemToRename.id, type: this.itemToRename.type, path: this.itemToRename.path, name: newName})

        this.itemToRename.name = newName
        const item = this.files.find(f => f.uid === this.itemToRename.uid)
        if (item) {
          item.name = newName
        }
      }
      this.showRenameModal = false
    },

    handleAddRevision(item) {
      if (!this.checkPermission(['create_files'], 'subir revisiones')) {
        return
      }
      this.revisionFileItem = item
      this.showAddRevisionDialog = true
      this.contextMenu.visible = false
    },

    uploadRevision(file) {
      if (!this.revisionFileItem || !file) {
        return
      }
      
      const fileData = {
        type: 14,
        payload: {
          main_file_revision_id: this.revisionFileItem?.revision_parent?.main_file_id || this.revisionFileItem.id,
					last_revision_file_id: this.revisionFileItem?.revision_parent?.file_id,
          project_id: this.selectedProject.id,
					folder_id: this.currentFolderId
        },
        description: `Revisión de ${this.revisionFileItem.name}`,
        total: file.size,
        uuid: uuidv4(),
        file
      }
      
      this.$store.globalStore.commit('setFilesToUploadCenter', fileData)
      
      this.setMessage({
        message: 'La revisión se está subiendo',
        title: 'Subiendo revisión',
        created: new Date(),
        type: 'success',
        icon: 'mdi-check-circle',
        color: '#3EBFA3'
      })
      
      this.showAddRevisionDialog = false
      this.revisionFileItem = null
    },

		setMessage(messageData) {
			const data = {
				message: messageData.message,
				title: messageData.title,
				created: new Date(),
				type: messageData.type,
				icon: messageData.icon,
				color: messageData.color,
			}
			this.$store.globalStore.dispatch('addMessage', data)
		}
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --fm-primary:         #2D4B95;
  --fm-primary-dark:    #213872;
  --fm-secondary:       #5E85EF;
  --fm-accent-glow:     rgba(94,133,239,0.13);
  --fm-accent:          var(--fm-secondary);
  --fm-accent2:         var(--fm-primary);
  --fm-bg:              #EEF1F8;
  --fm-surface:         #FFFFFF;
  --fm-surface2:        #F4F6FB;
  --fm-surface3:        #E8ECF5;
  --fm-surface4:        #D5DBED;
  --fm-border:          rgba(45,75,149,0.09);
  --fm-border2:         rgba(45,75,149,0.17);
  --fm-text:            #18243d;
  --fm-text2:           #576080;
  --fm-text3:           #99a5be;
  --fm-red:             #B03A2E;
  --fm-green:           #1A7A42;
  --fm-yellow:          #C47F17;
  --fm-radius:          10px;
  --fm-radius-sm:       6px;
  --fm-radius-xs:       4px;
  --fm-shadow:          0 2px 16px rgba(45,75,149,0.10);
  --fm-shadow-lg:       0 8px 40px rgba(45,75,149,0.16);
  --fm-font:            'DM Sans', sans-serif;
  --fm-mono:            'DM Mono', monospace;
  --fm-sidebar-w:       220px;
  --fm-toolbar-h:       56px;
  --fm-statusbar-h:     32px;
}
.fm-root { font-family: var(--fm-font); background: var(--fm-bg); color: var(--fm-text); height: 100%; width: 100%; display: flex; flex-direction: column; overflow: hidden; position: relative; }
.fm-root.fm-drag-over::after { content: ''; position: absolute; inset: 0; border: 2px dashed var(--fm-secondary); border-radius: var(--fm-radius); background: var(--fm-accent-glow); pointer-events: none; z-index: 100; }
.fm-body { display: flex; flex: 1; overflow: hidden; }
.fm-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--fm-surface4); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: var(--fm-secondary); }
.fm-loader {
  position: absolute;
  inset: 0;
  background: rgba(94, 133, 239, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border-radius: var(--fm-radius);
}
.fm-folder-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.fm-document-viewer {
  max-height: 90vh;
  overflow: auto;
}
.fm-document-viewer .v-card__title {
  background: var(--fm-surface2);
  color: var(--fm-text);
  padding: 16px;
}
.fm-document-content {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 600px;
}
</style>
